import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Loader2, 
  Trash2, 
  Edit2, 
  X,
  Tag
} from 'lucide-react';
import { notify } from '../services/notify';
import { 
  db, 
  auth, 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  where
} from '../firebase';
import { cn } from '../lib/utils';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  console.error('Firestore Error: ', error);
  notify.error(`Error: ${error instanceof Error ? error.message : 'An unexpected error occurred'}`);
}

interface TagItem {
  id: string;
  name: string;
  color: string;
  type: 'purchases' | 'sales' | 'contacts';
  orgId: string;
  createdBy: string;
  createdAt: any;
}

interface TagsViewProps {
  orgId: string;
  language: 'en' | 'ar';
}

const COLORS = [
  { name: 'Gray', value: '#6B7280' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Rose', value: '#F43F5E' },
];

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());
const getLocalApiHeaders = () => ({
  Authorization: `Bearer ${getLocalAuthToken()}`,
  'Content-Type': 'application/json'
});

export default function TagsView({ orgId, language = 'en' }: TagsViewProps) {
  const [tags, setTags] = useState<TagItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);
  const [editingTag, setEditingTag] = useState<TagItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'purchases' | 'sales' | 'contacts'>('contacts');
  
  const [newTag, setNewTag] = useState({
    name: '',
    color: COLORS[0].value,
    type: 'contacts' as 'purchases' | 'sales' | 'contacts'
  });

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setLoading(true);
      void fetch(`/api/organizations/${orgId}/tags`, { headers: getLocalApiHeaders() })
        .then(async response => {
          const payload = await response.json().catch(() => null);
          if (!response.ok) throw new Error(payload?.error || 'Could not load tags');
          return payload as TagItem[];
        })
        .then(tagList => {
          if (!cancelled) setTags(tagList);
        })
        .catch(error => {
          console.error('Local API error loading tags:', error);
          if (!cancelled) notify.error(error instanceof Error ? error.message : 'Failed to load tags');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'tags'), 
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tagList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TagItem));
      setTags(tagList);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tags');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [orgId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTag.name.trim()) {
      notify.error('Tag name is required');
      return;
    }

    try {
      if (isLocalSession()) {
        const endpoint = editingTag
          ? `/api/organizations/${orgId}/tags/${editingTag.id}`
          : `/api/organizations/${orgId}/tags`;
        const response = await fetch(endpoint, {
          method: editingTag ? 'PATCH' : 'POST',
          headers: getLocalApiHeaders(),
          body: JSON.stringify(newTag)
        });
        const saved = await response.json().catch(() => null) as TagItem | null;
        if (!response.ok || !saved) throw new Error((saved as any)?.error || 'Could not save tag');
        setTags(current => editingTag
          ? current.map(tag => tag.id === saved.id ? saved : tag)
          : [saved, ...current]
        );
      } else if (editingTag) {
        const tagRef = doc(db, 'tags', editingTag.id);
        await updateDoc(tagRef, {
          ...newTag,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'tags'), {
          ...newTag,
          orgId,
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp()
        });
      }
      notify.confirm(editingTag ? 'Tag updated' : 'Tag added');
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      if (isLocalSession()) {
        console.error('Local API error saving tag:', error);
        notify.error(error instanceof Error ? error.message : 'Failed to save tag');
      } else {
        handleFirestoreError(error, editingTag ? OperationType.UPDATE : OperationType.CREATE, 'tags');
      }
    }
  };

  const resetForm = () => {
    setNewTag({
      name: '',
      color: COLORS[0].value,
      type: activeTab
    });
    setEditingTag(null);
  };

  const handleEdit = (tag: TagItem) => {
    setEditingTag(tag);
    setNewTag({
      name: tag.name,
      color: tag.color,
      type: tag.type || 'contacts'
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!tagToDelete) return;
    try {
      if (isLocalSession()) {
        const response = await fetch(`/api/organizations/${orgId}/tags/${tagToDelete}`, {
          method: 'DELETE',
          headers: getLocalApiHeaders()
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error || 'Could not delete tag');
        }
        setTags(current => current.filter(tag => tag.id !== tagToDelete));
      } else {
        await deleteDoc(doc(db, 'tags', tagToDelete));
      }
      notify.confirm('Tag deleted');
      setIsDeleteModalOpen(false);
      setTagToDelete(null);
    } catch (error) {
      if (isLocalSession()) {
        console.error('Local API error deleting tag:', error);
        notify.error(error instanceof Error ? error.message : 'Failed to delete tag');
      } else {
        handleFirestoreError(error, OperationType.DELETE, 'tags');
      }
    }
  };

  const filteredTags = tags
    .filter(tag => !tag.type || tag.type === activeTab || (tag.type !== 'purchases' && tag.type !== 'sales' && tag.type !== 'contacts' && activeTab === 'contacts'))
    .filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#141414]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-end">
        <div className="space-y-1">
          <h1 className="text-4xl font-bold tracking-tighter text-[#141414] uppercase">Tags Setup</h1>
          <p className="text-sm font-mono text-[#141414]/60 uppercase tracking-widest">Manage tags to categorize your data</p>
        </div>
      </div>

      <div className="flex gap-4 border-b-2 border-[#141414]/10">
        {(['contacts', 'sales', 'purchases'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setNewTag(prev => ({ ...prev, type: tab }));
            }}
            className={cn(
              "px-6 py-3 font-bold text-sm uppercase tracking-widest transition-all border-b-4",
              activeTab === tab 
                ? "border-[#141414] text-[#141414]" 
                : "border-transparent text-[#141414]/40 hover:text-[#141414]/60"
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="group relative flex flex-col items-center justify-center p-8 bg-[#E4E3E0] border-2 border-dashed border-[#141414]/20 rounded-2xl hover:border-[#141414] hover:bg-[#141414] transition-all duration-300 overflow-hidden"
        >
          <div className="relative z-10 flex flex-col items-center gap-4 group-hover:text-[#E4E3E0] transition-colors">
            <div className="p-4 bg-[#141414]/5 rounded-full group-hover:bg-[#E4E3E0]/10">
              <Plus className="w-8 h-8 text-[#141414] group-hover:text-[#E4E3E0]" />
            </div>
            <div className="text-center">
              <span className="block text-lg font-bold italic serif">New Tag</span>
              <span className="block text-[10px] uppercase font-bold opacity-70 group-hover:opacity-90">Create a new classification tag</span>
            </div>
          </div>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#141414]/5 rounded-full blur-2xl group-hover:bg-[#E4E3E0]/5 transition-all" />
        </button>

        <div className="md:col-span-3 bg-[#E4E3E0] border-2 border-[#141414] rounded-2xl p-6 flex flex-col justify-between shadow-[8px_8px_0px_0px_rgba(20,20,20,1)]">
          <div className="space-y-4">
            <label className="block text-[10px] font-bold uppercase tracking-widest opacity-70">Search Tags</label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#141414]/40" />
              <input
                type="text"
                placeholder="Search by tag name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white/50 border-2 border-[#141414] rounded-xl text-[#141414] font-bold placeholder:text-[#141414]/50 focus:outline-none focus:ring-4 focus:ring-[#141414]/5 transition-all"
              />
            </div>
          </div>
          <div className="flex gap-8 pt-4 border-t border-[#141414]/10">
            <div>
              <span className="block text-[10px] font-bold uppercase opacity-70">Total Tags</span>
              <span className="text-2xl font-mono font-bold text-[#141414]">{tags.length}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#E4E3E0] border-2 border-[#141414] rounded-2xl overflow-hidden shadow-[12px_12px_0px_0px_rgba(20,20,20,1)]">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#141414] tracking-widest border-r-2 border-[#141414]/10">Tag Name</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#141414] tracking-widest border-r-2 border-[#141414]/10">Color</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#141414] tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y-2 divide-[#141414]/10">
              {filteredTags.map((tag) => (
                <tr key={tag.id} className="hover:bg-[#141414]/5 transition-colors group">
                  <td className="px-6 py-4 border-r-2 border-[#141414]/10">
                    <div className="flex items-center gap-3">
                      <Tag className="w-4 h-4" style={{ color: tag.color }} />
                      <div className="text-lg font-bold italic serif text-[#141414]">{tag.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 border-r-2 border-[#141414]/10">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full border border-[#141414]/20" style={{ backgroundColor: tag.color }} />
                      <span className="text-xs font-mono opacity-60 uppercase">{tag.color}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => handleEdit(tag)}
                        className="p-2 bg-white border-2 border-[#141414] rounded-xl text-[#141414] hover:bg-[#141414] hover:text-[#E4E3E0] transition-all shadow-[4px_4px_0px_0px_rgba(20,20,20,1)] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setTagToDelete(tag.id);
                          setIsDeleteModalOpen(true);
                        }}
                        className="p-2 bg-white border-2 border-[#141414] rounded-xl text-red-600 hover:bg-red-600 hover:text-white transition-all shadow-[4px_4px_0px_0px_rgba(20,20,20,1)] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredTags.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-4 opacity-30">
                      <Tag className="w-12 h-12" />
                      <p className="text-xl font-bold italic serif">No tags found.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border-2 border-[#141414] rounded-3xl shadow-[16px_16px_0px_0px_rgba(20,20,20,1)] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain animate-in zoom-in duration-300">
            <div className="px-8 py-6 border-b-2 border-[#141414] flex justify-between items-center">
              <h2 className="text-2xl font-bold italic serif text-[#141414]">
                {editingTag ? 'Edit Tag' : 'New Tag'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#141414] mb-2 opacity-70">Tag Name</label>
                <input
                  type="text"
                  required
                  value={newTag.name}
                  onChange={(e) => setNewTag(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Urgent, Intercompany"
                  className="w-full px-4 py-3 bg-white/50 border-2 border-[#141414] rounded-xl text-[#141414] font-bold focus:outline-none focus:ring-4 focus:ring-[#141414]/5 transition-all"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#141414] mb-2 opacity-70">Tag Color</label>
                <div className="grid grid-cols-5 gap-3">
                  {COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setNewTag(prev => ({ ...prev, color: color.value }))}
                      className={cn(
                        "w-full aspect-square rounded-xl border-2 transition-all",
                        newTag.color === color.value ? "border-[#141414] scale-110 shadow-lg" : "border-transparent opacity-60 hover:opacity-100"
                      )}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-6 py-4 border-2 border-[#141414] text-[#141414] rounded-full font-bold text-xs uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-4 bg-[#141414] text-[#E4E3E0] rounded-full font-bold text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)]"
                >
                  {editingTag ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsDeleteModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border-2 border-[#141414] rounded-[2rem] shadow-[16px_16px_0px_0px_rgba(20,20,20,1)] w-full max-w-sm max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain p-8 space-y-6 animate-in zoom-in duration-300">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold italic serif text-[#141414]">Confirm Delete</h3>
              <p className="text-sm font-bold uppercase tracking-tight opacity-70">Are you sure you want to delete this tag? This action cannot be undone.</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 px-4 py-4 border-2 border-[#141414] text-[#141414] rounded-full font-bold text-xs uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-4 bg-red-600 text-white border-2 border-red-600 rounded-full font-bold text-xs uppercase tracking-widest hover:bg-red-700 transition-all shadow-[4px_4px_0px_0px_rgba(220,38,38,0.2)]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
